export interface IntentSignal {
  icon: string;
  title: string;
  description: string;
}

const HIGH_PROBABILITY_WINDOW_HOURS = 16;
const ESCALATION_WINDOW_HOURS = 48;

const INACTIVE_STATUSES = ['deleted', 'cancelled'];

const parseVisitDateTime = (visitDate: string, visitTime: string): Date | null => {
  const dateParts = visitDate.split('/');
  if (dateParts.length !== 3) return null;
  const [day, month, year] = dateParts.map(Number);

  const timeMatch = visitTime.match(/(\d{1,2}):(\d{2})\s?(AM|PM)/i);
  if (!timeMatch) return null;
  const [, hourStr, minuteStr, meridiem] = timeMatch;
  let hour = Number(hourStr) % 12;
  if (meridiem.toUpperCase() === 'PM') hour += 12;
  const minute = Number(minuteStr);

  const parsed = new Date(year, month - 1, day, hour, minute);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getHoursSinceVisit = (
  visitDate: string,
  visitTime: string,
): number | null => {
  const visitDateTime = parseVisitDateTime(visitDate, visitTime);
  if (!visitDateTime) return null;
  return (Date.now() - visitDateTime.getTime()) / (1000 * 60 * 60);
};

export const getIntentSignal = (
  visitDate: string,
  visitTime: string,
  status: string,
  hasFavorites: boolean,
): IntentSignal | null => {
  const isInactive = INACTIVE_STATUSES.includes(status);
  if (isInactive) {
    return hasFavorites
      ? {
          icon: '❤️',
          title: 'Está comparando opciones',
          description:
            'La visita/asesoría fue cancelada, pero tiene favoritos guardados: sigue comparando.',
        }
      : null;
  }

  const hoursSince = getHoursSinceVisit(visitDate, visitTime);

  if (hoursSince !== null && hoursSince >= 0) {
    if (hoursSince <= HIGH_PROBABILITY_WINDOW_HOURS) {
      return {
        icon: '🔥',
        title: 'Alta probabilidad de oferta',
        description: `Visitó hace ${Math.round(
          hoursSince,
        )} h. Agendar ya lo hace ~200× más probable de ofertar; la escalada típica es en ~16 h — contáctalo ahora.`,
      };
    }

    if (hoursSince <= ESCALATION_WINDOW_HOURS) {
      return {
        icon: '⏱️',
        title: 'Ventana de escalada abierta',
        description: `Visitó hace ${Math.round(
          hoursSince,
        )} h. El promedio de escalada es ~16 h: el seguimiento de hoy vale más que el de la próxima semana.`,
      };
    }

    return {
      icon: '📉',
      title: 'Señal enfriándose',
      description: `Visitó hace ${Math.round(
        hoursSince,
      )} h sin escalar. Pasadas 48 h la probabilidad de conversión baja — retómalo igual, pero sin urgencia.`,
    };
  }

  if (hoursSince !== null && hoursSince < 0) {
    return {
      icon: '📅',
      title: 'Visita agendada = alta intención',
      description:
        'Agendar ya lo hace ~200× más probable de reservar u ofertar que el resto. Da seguimiento el mismo día de la visita.',
    };
  }

  if (hasFavorites) {
    return {
      icon: '❤',
      title: 'Está comparando opciones',
      description:
        'Tiene favoritos guardados: está comparando — pregúntale por ellos para orientar la conversación.',
    };
  }

  return null;
};
